import { useState } from 'react'
import { motion } from 'framer-motion'
import { cn } from '../../utils/cn'

const footerLinks = {
  shop: [
    { label: 'All Products', href: '#products' },
    { label: 'Collections', href: '#collections' },
    { label: 'New Arrivals', href: '#' },
    { label: 'Best Sellers', href: '#' },
  ],
  company: [
    { label: 'About Us', href: '#about' },
    { label: 'Contact', href: '#contact' },
    { label: 'Careers', href: '#' },
    { label: 'Press', href: '#' },
  ],
  support: [
    { label: 'FAQ', href: '#' },
    { label: 'Shipping', href: '#' },
    { label: 'Returns', href: '#' },
    { label: 'Size Guide', href: '#' },
  ],
  legal: [
    { label: 'Privacy Policy', href: '#' },
    { label: 'Terms of Service', href: '#' },
    { label: 'Cookie Policy', href: '#' },
  ],
}

const socialLinks = [
  { label: 'Instagram', href: '#' },
  { label: 'TikTok', href: '#' },
  { label: 'Pinterest', href: '#' },
]

// Newsletter signup component
function NewsletterSignup() {
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState('idle')

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!email) return
    setStatus('loading')
    setTimeout(() => {
      setStatus('success')
      setEmail('')
    }, 1000)
  }

  return (
    <div className="max-w-md">
      <h4 className="text-sm font-medium text-text-primary uppercase tracking-wider mb-3">
        Newsletter
      </h4>
      <p className="text-sm text-text-secondary mb-4">
        Subscribe for exclusive offers and new collection updates.
      </p>

      {status === 'success' ? (
        <p className="text-sm text-text-secondary">Thank you for subscribing.</p>
      ) : (
        <form onSubmit={handleSubmit} className="flex">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Your email address"
            className="flex-1 px-4 py-3 bg-transparent border border-border text-text-primary placeholder:text-text-muted text-sm focus:outline-none focus:border-primary transition-colors"
          />
          <button
            type="submit"
            disabled={status === 'loading'}
            className="px-6 py-3 bg-primary text-white text-sm font-medium uppercase tracking-wider hover:bg-primary-light transition-colors disabled:opacity-50"
          >
            {status === 'loading' ? '...' : 'Subscribe'}
          </button>
        </form>
      )}
    </div>
  )
}

// Footer link component
function FooterLink({ href, children }) {
  return (
    <a
      href={href}
      className="text-sm text-text-secondary hover:text-text-primary transition-colors duration-200"
    >
      {children}
    </a>
  )
}

// Footer section with title
function FooterSection({ title, links }) {
  return (
    <div>
      <h4 className="text-xs font-medium text-text-primary uppercase tracking-[0.15em] mb-4">
        {title}
      </h4>
      <ul className="space-y-3">
        {links.map((link) => (
          <li key={link.label}>
            <FooterLink href={link.href}>{link.label}</FooterLink>
          </li>
        ))}
      </ul>
    </div>
  )
}

export default function Footer() {
  return (
    <footer id="contact" className="bg-bg-secondary border-t border-border">
      {/* Main footer content */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-20">
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-8 lg:gap-12">
          {/* Brand section */}
          <div className="col-span-2">
            <a href="/" className="inline-block mb-6">
              <span className="text-xl font-display font-bold text-text-primary tracking-wide">
                Nakhoons
              </span>
            </a>
            <p className="text-sm text-text-secondary max-w-xs mb-8">
              Premium smart press-on nails with NFC technology. Where fashion meets innovation.
            </p>

            {/* Social links */}
            <div className="flex gap-6">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  className="text-sm text-text-secondary hover:text-text-primary transition-colors uppercase tracking-wider"
                  aria-label={social.label}
                >
                  {social.label}
                </a>
              ))}
            </div>
          </div>

          {/* Link sections */}
          <FooterSection title="Shop" links={footerLinks.shop} />
          <FooterSection title="Company" links={footerLinks.company} />
          <FooterSection title="Support" links={footerLinks.support} />

          {/* Newsletter section */}
          <div className="col-span-2 lg:col-span-2">
            <NewsletterSignup />
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
          <div className="flex flex-col lg:flex-row justify-between items-center gap-4">
            <p className="text-xs text-text-muted">
              &copy; {new Date().getFullYear()} Nakhoons. All rights reserved.
            </p>
            <div className="flex gap-6">
              {footerLinks.legal.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  className="text-xs text-text-muted hover:text-text-secondary transition-colors"
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}
